import { useState, useCallback } from 'react';
import type { FC, CSSProperties } from 'react';
import classnames from 'classnames';

import Icon from 'app/components/Icon';
import avatarIcon from 'app/icons/avatar_panda.svg';

import style from './style.scss';
import { getUserInitials, getUserPPColor } from './utils';

interface Props {
  size?: number;
  user: { id: number; image_url: string | null; email?: string; name?: string };
  className?: string;
  style?: CSSProperties;
}

function getStyle(size: number, css?: CSSProperties): CSSProperties {
  return {
    width: size,
    height: size,
    ...css
  };
}

const UserAvatar: FC<Props> = ({ size = 36, user, className, style: css }) => {
  const [status, setStatus] = useState<'loading' | 'succeed' | 'failed'>('loading');
  const handleSuccess = useCallback(() => {
    setStatus('succeed');
  }, []);
  const handleError = useCallback(() => {
    setStatus('failed');
  }, []);

  const containerStyle = getStyle(size, css);
  const containerClassName = classnames(style.container, className, {
    [style.container_loading]: status === 'loading'
  });

  return (
    <div className={containerClassName} style={containerStyle}>
      {user.image_url && status !== 'failed' ? (
        <img
          src={user.image_url}
          alt="User Avatar"
          className={style.image}
          onLoad={handleSuccess}
          onError={handleError}
        />
      ) : (
        <>
          {user.email ? (
            <div
              className={style.userNamePlaceholder}
              style={{ backgroundColor: getUserPPColor(user.email), fontSize: size / 2 }}
            >
              {getUserInitials({ email: user.email, name: user.name })}
            </div>
          ) : (
            <Icon href={avatarIcon} className={style.icon} />
          )}
        </>
      )}
    </div>
  );
};

UserAvatar.displayName = 'UserAvatar';

export default UserAvatar;
